import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useContainerWidth } from "react-grid-layout";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import type { DashboardProfile, Layout, ResponsiveLayouts } from "../../utils/dashboard.types";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./react-grid-layout.css";

import { useDashboardProfileDraft } from "../../hooks/useDashboardProfileDraft";
import { useDashboardProfiles } from "../../hooks/useDashboardProfiles";
import { useHseInstance } from "../../hooks/useHseInstance";
import { dashboardWidgetsOptions } from "../../api/queryApi";
import { CustomizationToolbar } from "../../components/DashboardWidgets/CustomizationToolbar";
import { ConfirmDeleteDialog } from "../../components/common/ConfirmDeleteDialog";
import { EmptyDashboardState } from "../../components/DashboardWidgets/EmptyDashboardState";
import { DashboardProfileNameDialog } from "../../components/DashboardWidgets/DashboardProfileNameDialog";
import { WidgetOverviewBar } from "../../components/DashboardWidgets/WidgetOverviewBar";
import { WidgetGrid } from "../../components/DashboardWidgets/WidgetGrid";
import { WidgetStack } from "../../components/DashboardWidgets/WidgetStack";
import { GRID_MIN_WIDTH } from "../../utils/breakpoints";
import { AVAILABLE_WIDGETS } from "../../components/DashboardWidgets/constants/widgets";
import { getRenderedDashboardWidgetIds } from "../../utils/dashboardProfileConfig";
import { PageHeaderRight } from "frontend-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useDashboardCustomization } from "@dashboard/hooks/useDashboardCustomization";
import { FeedbackLink } from "@dashboard/components/DashboardWidgets/FeedbackLink";

export function DashboardPage({
    customerId,
    profileId,
}: {
    customerId: string;
    profileId?: number;
}) {
    const { t } = useTranslation();
    const { productInstanceId } = useHseInstance();
    const navigate = useNavigate();
    const dashboardProfiles = useDashboardProfiles(
        productInstanceId,
        t("dashboard.myDashboard"),
        profileId,
    );
    const { data: widgetProvisioning = [], isPending: isWidgetProvisioningPending } = useQuery(
        dashboardWidgetsOptions(productInstanceId),
    );
    const shouldRedirectToDashboardRoot =
        profileId !== undefined &&
        dashboardProfiles.isError &&
        isAxiosError(dashboardProfiles.error) &&
        dashboardProfiles.error.response?.status === 404;

    useEffect(() => {
        if (!shouldRedirectToDashboardRoot) return;

        void navigate({
            to: "/$customerId/dashboard",
            params: { customerId },
            replace: true,
        });
    }, [customerId, navigate, shouldRedirectToDashboardRoot]);

    useEffect(() => {
        if (
            shouldRedirectToDashboardRoot ||
            !dashboardProfiles.profile ||
            profileId === dashboardProfiles.profile.id
        )
            return;

        void navigate({
            to: "/$customerId/dashboard/$profileId",
            params: {
                customerId,
                profileId: String(dashboardProfiles.profile.id),
            },
            replace: true,
        });
    }, [customerId, dashboardProfiles.profile, navigate, profileId, shouldRedirectToDashboardRoot]);

    const availableWidgets = useMemo(() => {
        const enabledWidgetIds = new Set<string>(
            widgetProvisioning
                .filter((provisioning) => provisioning.enabled)
                .map((provisioning) => provisioning.widget),
        );
        return AVAILABLE_WIDGETS.filter((widget) => enabledWidgetIds.has(widget.id));
    }, [widgetProvisioning]);

    const handleDeleteProfile = async (profile: DashboardProfile) => {
        const nextProfile = dashboardProfiles.profiles.find(
            (dashboardProfile) => dashboardProfile.id !== profile.id,
        );

        if (!nextProfile) {
            throw new Error("Cannot delete the final dashboard profile.");
        }

        await dashboardProfiles.deleteProfile(profile.id);
        await navigate({
            to: "/$customerId/dashboard/$profileId",
            params: {
                customerId,
                profileId: String(nextProfile.id),
            },
            replace: true,
        });
    };

    if (dashboardProfiles.isPending || isWidgetProvisioningPending) {
        return <DashboardStatus>{t("common.loading")}</DashboardStatus>;
    }

    if (dashboardProfiles.isError || !dashboardProfiles.profile) {
        return (
            <DashboardStatus>
                {t("common.somethingWentWrongContactYourSystemAdministratorForMoreInformation")}
            </DashboardStatus>
        );
    }

    if (profileId === undefined) {
        return <DashboardStatus>{t("common.loading")}</DashboardStatus>;
    }

    const profile = dashboardProfiles.profile;

    return (
        <DashboardProfileEditor
            key={profile.id}
            profile={profile}
            availableWidgets={availableWidgets}
            isSaving={dashboardProfiles.isSaving}
            isRenaming={dashboardProfiles.isRenaming}
            canDeleteProfile={dashboardProfiles.profiles.length > 1}
            onSave={(config) =>
                dashboardProfiles.saveProfile({
                    profileId: profile.id,
                    config,
                })
            }
            onRename={(name) => dashboardProfiles.renameProfile({ profileId: profile.id, name })}
            onDelete={handleDeleteProfile}
        />
    );
}

function DashboardProfileEditor({
    profile,
    availableWidgets,
    isSaving,
    isRenaming,
    canDeleteProfile,
    onSave,
    onRename,
    onDelete,
}: {
    profile: DashboardProfile;
    availableWidgets: typeof AVAILABLE_WIDGETS;
    isSaving: boolean;
    isRenaming: boolean;
    canDeleteProfile: boolean;
    onSave: (config: DashboardProfile["config"]) => Promise<DashboardProfile>;
    onRename: (name: string) => Promise<DashboardProfile>;
    onDelete: (profile: DashboardProfile) => Promise<void>;
}) {
    const { t } = useTranslation();
    const [isCustomizing, setIsCustomizing] = useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const widgetOverviewId = useId();
    const { width, containerRef } = useContainerWidth();
    const { setHasUnsavedChanges } = useDashboardCustomization();
    const {
        draft,
        addWidget,
        removeWidget,
        addWidgetToLayout,
        handleLayoutChange,
        reset,
        discardChanges,
        isDirty,
    } = useDashboardProfileDraft(profile.config, width);

    useEffect(() => {
        setHasUnsavedChanges(isCustomizing && isDirty);
    }, [isCustomizing, isDirty, setHasUnsavedChanges]);

    useEffect(() => () => setHasUnsavedChanges(false), [setHasUnsavedChanges]);

    const availableWidgetIds = useMemo(
        () => new Set(availableWidgets.map((widget) => widget.id)),
        [availableWidgets],
    );
    const renderedActiveWidgets = useMemo(
        () => getRenderedDashboardWidgetIds(draft.activeWidgets, availableWidgetIds),
        [availableWidgetIds, draft.activeWidgets],
    );

    const handleToggleWidget = useCallback(
        (widgetId: string) => {
            if (draft.activeWidgets.includes(widgetId)) {
                removeWidget(widgetId);
            } else {
                addWidget(widgetId);
                addWidgetToLayout(widgetId);
            }
        },
        [draft.activeWidgets, addWidget, addWidgetToLayout, removeWidget],
    );

    const handleSave = async () => {
        try {
            await onSave(draft);
            setIsCustomizing(false);
        } catch {
            toast.error(t("dashboard.profileConfigurationSaveError"));
        }
    };

    const handleCancel = () => {
        discardChanges();
        setIsCustomizing(false);
    };

    return (
        <div className="relative">
            <PageHeaderRight>
                <CustomizationToolbar
                    isCustomizing={isCustomizing}
                    isSaving={isSaving}
                    canDeleteProfile={canDeleteProfile}
                    onStartCustomizing={() => setIsCustomizing(true)}
                    onRenameProfile={() => setIsRenameDialogOpen(true)}
                    onDeleteProfile={() => setIsDeleteDialogOpen(true)}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    controlsId={widgetOverviewId}
                />
                <FeedbackLink />
            </PageHeaderRight>

            <DashboardProfileNameDialog
                mode="rename"
                open={isRenameDialogOpen}
                profileName={profile.name}
                isSubmitting={isRenaming}
                onOpenChange={setIsRenameDialogOpen}
                onSubmit={onRename}
            />

            <ConfirmDeleteDialog
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                onConfirm={async () => {
                    try {
                        await onDelete(profile);
                    } catch (error) {
                        toast.error(t("dashboard.profileDeleteError"));
                        throw error;
                    }
                }}
                title={t("dashboard.deleteProfile")}
                description={t("dashboard.confirmDeleteProfile", { name: profile.name })}
            />

            <WidgetOverviewBar
                id={widgetOverviewId}
                isOpen={isCustomizing}
                allWidgets={availableWidgets}
                activeWidgetIds={renderedActiveWidgets}
                onToggleWidget={handleToggleWidget}
                onResetLayout={reset}
            />

            <div ref={containerRef}>
                {renderedActiveWidgets.length === 0 ? (
                    <EmptyDashboardState
                        isCustomizing={isCustomizing}
                        onStartCustomizing={() => setIsCustomizing(true)}
                    />
                ) : width === 0 ? null : width < GRID_MIN_WIDTH ? (
                    <WidgetStack
                        activeWidgets={renderedActiveWidgets}
                        isCustomizing={isCustomizing}
                        onRemoveWidget={removeWidget}
                    />
                ) : (
                    <WidgetGrid
                        activeWidgets={renderedActiveWidgets}
                        layouts={draft.layouts}
                        isCustomizing={isCustomizing}
                        width={width}
                        onLayoutChange={(_layout: Layout, allLayouts) => {
                            if (isCustomizing) {
                                handleLayoutChange(_layout, allLayouts as ResponsiveLayouts);
                            }
                        }}
                        onRemoveWidget={removeWidget}
                    />
                )}
            </div>
        </div>
    );
}

function DashboardStatus({ children }: { children: string }) {
    return (
        <div className="text-compendia-muted flex min-h-[400px] items-center justify-center">
            {children}
        </div>
    );
}
